<template>
  <div v-if="shouldRenderComponent" class="user-quota marg-1-bottom text-dark" :class="{'user-quota--sidebar': isSidebar}">
    <div class="user-quota__body">
      <table class="d-table user-quota__table">
        <thead>
        <tr>
          <th>
            <div class="user-quota__table-header">
				<div class="user-quota__title-group">
					<h4 class="user-quota__title">{{ i18n.userQuota }}</h4>
					<div class="user-quota__employment" v-if="employments.length > 0">
						<select class="do-select" v-model="selectedEmploymentId">
						<option
							v-for="employment in employments"
							:key="employment.id"
							:value="employment.id">
							{{ employment.title }} - {{ employment.employment_number || employment.id }}
						</option>
						</select>
					</div>
				</div>
				<div class="user-quota__subtitle">Saldo i KMD pr. {{ quotaDateLabel }}</div>
            </div>
          </th>
          <th class="is-number">
			{{ i18n.balance }}
			</th>
          <th class="is-number">{{ i18n.used }}</th>
          <th class="is-number">{{ i18n.difference }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="isLoadingEmployments || isLoadingQuotas">
          <td colspan="4" class="user-quota__status-cell">
            <div class="user-quota__loading">
              <div class="do-spinner"></div>
            </div>
          </td>
        </tr>
        <tr v-else-if="errorMessage">
          <td colspan="4" class="user-quota__status-cell">
            <div class="user-quota__error">
              {{ errorMessage }}
            </div>
          </td>
        </tr>
        <tr v-else-if="hasLoadedQuotas && sortedQuotas.length === 0">
          <td colspan="4" class="user-quota__status-cell">
            <p class="user-quota__empty">
              {{ i18n.noQuotas }}
            </p>
          </td>
        </tr>
        <tr v-for="quota in sortedQuotas" v-else :key="quota.QuotaNumber">
          <td>
            <div class="user-quota__quota-title">{{ quota.QuotaTypaText || quota.QuotaType }}</div>
            <div class="user-quota__quota-meta">
              {{ i18n.deductionPeriod }}: {{ formatDateRange(quota.DeductionStartDate, quota.DeductionEndDate) }}
            </div>
          </td>
          <td class="is-number">{{ formatDecimal(quota.QuotaAccrued) }}</td>
          <td class="is-number">{{ formatDecimal(quota.QuotaUsed) }}</td>
          <td class="is-number">{{ formatDecimal(quota.QuotaValue) }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import docendo from '../../../docendo'
import moment from 'moment'

const I18N = {
  da: {
    userQuota: 'Kvoter',
    showingForDate: 'Viser saldo pr.',
    employment: 'Ansættelse',
    quotaType: 'Kontotype',
    balance: 'Saldo',
    used: 'Brugt',
    difference: 'Forskel',
    deductionPeriod: 'Afholdelsesperiode',
    noQuotas: 'Der blev ikke fundet nogen kvoter for den valgte ansættelse.',
    loadError: 'Kunne ikke hente kvoter fra KMD.'
  },
  nb: {
    userQuota: 'KMD-kvoter',
    showingForDate: 'Viser saldo per',
    employment: 'Ansettelse',
    quotaType: 'Kontotype',
    balance: 'Saldo',
    used: 'Brukt',
    difference: 'Forskjell',
    deductionPeriod: 'Uttaksperiode',
    noQuotas: 'Fant ingen kvoter for valgt ansettelse.',
    loadError: 'Kunne ikke hente kvoter fra KMD.'
  },
  en: {
    userQuota: 'KMD quotas',
    showingForDate: 'Showing balance for',
    employment: 'Employment',
    quotaType: 'Quota type',
    balance: 'Balance',
    used: 'Used',
    difference: 'Difference',
    deductionPeriod: 'Deduction period',
    noQuotas: 'No quotas were found for the selected employment.',
    loadError: 'Could not fetch quotas from KMD.'
  }
}

export default {
  props: {
    userId: {
      type: Number,
      default: null
    },
    date: {
      default: null
    },
    isSidebar: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedEmploymentId: null,
      quotas: [],
      isLoadingQuotas: false,
      hasLoadedQuotas: false,
      errorMessage: '',
      userEmploymentsRaw: [],
      isLoadingEmployments: false,
      hasRequestedEmployments: false
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale || 'da'
    },
    i18n () {
      return I18N[this.locale] || I18N.da
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    employments () {
      const out = []
      for (let i = 0; i < this.userEmploymentsRaw.length; i++) {
        const employment = this.userEmploymentsRaw[i]
        const employmentNumber = employment && employment.employment_number ? String(employment.employment_number) : ''
        if (employmentNumber.trim() !== '') {
          out.push(employment)
        }
      }
      return out
    },
    shouldRenderComponent () {
      if (!this.hasRequestedEmployments || this.isLoadingEmployments) {
        return true
      }
      return this.employments.length > 0
    },
    effectiveDate () {
      // Brug altid dags dato til kaldet mod KMD
      return moment().format('YYYY-MM-DD')
    },
    quotaDateLabel () {
      return this.effectiveDate ? moment(this.effectiveDate, 'YYYY-MM-DD').format('DD-MM-YYYY') : ''
    },
    sortedQuotas () {
      const MAX_END_DATE = '9999-12-31'
      return this.quotas.slice().sort((a, b) => {
        const aEnd = a && a.DeductionEndDate ? a.DeductionEndDate : ''
        const bEnd = b && b.DeductionEndDate ? b.DeductionEndDate : ''

        const aIsMax = aEnd === MAX_END_DATE
        const bIsMax = bEnd === MAX_END_DATE

        if (aIsMax && !bIsMax) {
          return 1
        }
        if (!aIsMax && bIsMax) {
          return -1
        }

        if (aEnd && bEnd && aEnd !== bEnd) {
          return aEnd.localeCompare(bEnd)
        }

        const aName = (a && (a.QuotaTypaText || a.QuotaType)) ? (a.QuotaTypaText || a.QuotaType) : ''
        const bName = (b && (b.QuotaTypaText || b.QuotaType)) ? (b.QuotaTypaText || b.QuotaType) : ''
        return aName.localeCompare(bName)
      })
    }
  },
  watch: {
    shouldRenderComponent: {
      immediate: true,
      handler (value) {
        this.$emit('visibility', value)
      }
    },
    employments () {
      this.selectDefaultEmployment()
    },
    selectedEmploymentId () {
      this.fetchQuotas()
    },
    date () {
      this.fetchQuotas()
    },
    userId () {
      this.readUserEmployments()
    }
  },
  methods: {
    readUserEmployments () {
      this.hasRequestedEmployments = true
      this.userEmploymentsRaw = []
      if (!this.userId || !this.schoolId) {
        return
      }
      const userId = this.userId
      this.isLoadingEmployments = true
      this.errorMessage = ''
      docendo.employmentsForUser(this.schoolId, userId, data => {
        if (userId !== this.userId) {
          return
        }
        this.userEmploymentsRaw = Array.isArray(data) ? data.filter(employment => employment.user_id * 1 === userId * 1) : []
        this.isLoadingEmployments = false
      }, error => {
        if (userId !== this.userId) {
          return
        }
        this.isLoadingEmployments = false
        this.handleQuotaError((error && error.error) || this.i18n.loadError)
      })
    },
    selectDefaultEmployment () {
      if (this.employments.length < 1) {
        this.selectedEmploymentId = null
        return
      }
      const selectedExists = this.employments.some(employment => String(employment.id) === String(this.selectedEmploymentId))
      if (!selectedExists) {
        this.selectedEmploymentId = this.employments[0].id
      }
    },
    fetchQuotas () {
      if (!this.selectedEmploymentId || !this.schoolId) {
        this.quotas = []
        this.hasLoadedQuotas = false
        this.errorMessage = ''
        return
      }

      this.isLoadingQuotas = true
      this.hasLoadedQuotas = false
      this.errorMessage = ''

      docendo.listQuotas(
        this.schoolId,
        [this.selectedEmploymentId],
        this.effectiveDate,
        response => {
          if (response && response.success === false) {
            this.handleQuotaError((response.data && response.data.error) || response.error || this.i18n.loadError)
            return
          }
          if (response && response.error) {
            this.handleQuotaError(response.error)
            return
          }
          if (response && response.data && response.data.error) {
            this.handleQuotaError(response.data.error)
            return
          }

          let parsedData = response
          if (response && response.data && response.data.response && typeof response.data.response === 'string') {
            try {
              parsedData = JSON.parse(response.data.response)
            } catch (e) {
              this.handleQuotaError(this.i18n.loadError)
              return
            }
          } else if (response && response.response && typeof response.response === 'string') {
            try {
              parsedData = JSON.parse(response.response)
            } catch (e) {
              this.handleQuotaError(this.i18n.loadError)
              return
            }
          }

          let quotaItems = null
          if (parsedData && parsedData.Body && parsedData.Body.FetchQuotaListResponse_MT) {
            const quotaList = parsedData.Body.FetchQuotaListResponse_MT.OT_QUOTA_LIST
            if (quotaList) {
              quotaItems = typeof quotaList.item !== 'undefined' ? quotaList.item : quotaList.Item
            }
          }

          if (Array.isArray(quotaItems)) {
            this.quotas = quotaItems
          } else if (quotaItems && typeof quotaItems === 'object') {
            this.quotas = [quotaItems]
          } else {
            this.quotas = []
          }

          this.isLoadingQuotas = false
          this.hasLoadedQuotas = true
        },
        error => {
          const errorMessage = (error && error.data && error.data.error) || (error && error.error) || this.i18n.loadError
          this.handleQuotaError(errorMessage)
        }
      )
    },
    handleQuotaError (message) {
      this.quotas = []
      this.errorMessage = message
      this.isLoadingQuotas = false
      this.hasLoadedQuotas = true
    },
    formatDate (date) {
      if (!date) {
        return ''
      }
      return moment(date, 'YYYY-MM-DD').isValid() ? moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY') : date
    },
    formatDateRange (startDate, endDate) {
      return this.formatDate(startDate) + ' - ' + this.formatDate(endDate)
    },
    formatDecimal (value) {
      if (value === null || typeof value === 'undefined' || value === '') {
        return '0'
      }
      const parsedValue = Number(value)
      if (isNaN(parsedValue)) {
        return String(value)
      }
      return parsedValue.toLocaleString(this.locale === 'en' ? 'en-US' : 'da-DK', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    }
  },
  mounted () {
    this.readUserEmployments()
  }
}
</script>

<style scoped>
.user-quota {
padding: 0;
  border: 1px solid #e2e8f0;
}

.user-quota__table-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  width: 100%;
}

.user-quota__title-group {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-quota__title {
  margin: 0 0 0.25rem 0;
}

.user-quota__subtitle {
  margin: 0;
  color: #8898aa;
  font-size: 0.875rem;
}

.user-quota__employment {
  min-width: 280px;
}

.user-quota__employment .do-select {
  width: 100%;
}

.user-quota__loading {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.user-quota__error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 0.75rem 1rem;
}

.user-quota__empty {
  margin: 0;
  color: #71717a;
  font-style: italic;
}

.user-quota__table {
  font-size: 10px;
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.user-quota__table thead th {
  background-color: #f6f9fc;
  padding: 0.25rem;
}

.user-quota__table thead th:not(:first-child) {
  color: #8898aa;
  text-transform: uppercase;
}

th:first-child {
  text-align: left;
}

.user-quota__table tbody tr {
  border: 1px solid #e2e8f0;
}

.user-quota__table tbody tr:nth-child(even) {
  background-color: #f8fafb;
}

.user-quota__table tbody tr td {
  padding: 0.25rem;
}

.user-quota__status-cell {
  padding: 0.75rem;
}

.user-quota__quota-title {
  font-weight: 600;
}

.user-quota__quota-meta {
	font-size: 0.8125rem;
    color: #71717a;
    line-height: 1;
}


.is-number {
  text-align: right;
}
.user-quota--sidebar .user-quota__table-header,
.user-quota--sidebar .user-quota__title-group {
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
}

.user-quota--sidebar .user-quota__employment {
  min-width: 0;
}

@media (max-width: 800px) {
  .user-quota__table-header {
    flex-direction: column;
    align-items: stretch;
  }

  .user-quota__employment {
    min-width: 0;
  }
}

@media only screen and (min-width: 500px) {
  .user-quota__table {
    font-size: 1rem;
  }
}
</style>



// WEBPACK FOOTER //
// UserQuota.vue?c988c69e